import { Box, List, Radio, Table, useMediaQuery, Theme } from "@mui/material";
import { useState } from "react";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import { replaceUnderScoreAddSpace } from "../../../../../../../utils/utils";
import { TableData } from "./AssessmentDrawer";
import { TitleDetail } from "./assessment-constant";
import { Title } from "@mui/icons-material";

interface AllScreeningAssessmentProps {
  tableData: TableData;
  handleRadioChange: (ind: number, val: string) => void;
  titleDetail: TitleDetail;
  disabled?: boolean;
}

const AllScreeningAssessment = ({
  tableData,
  titleDetail,
  handleRadioChange,
  disabled,
}: AllScreeningAssessmentProps) => {
  const [showScore, _setShowScore] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  // Mobile rendering
  const mobileRows = tableData?.screeners?.map((item: any, ind: number) => (
    <Box
      key={item.key}
      mb={2}
      p={2}
      borderRadius={2}
      border={"1px solid #efefef"}
      boxShadow={"0 1px 5px rgba(0,0,0,0.04)"}
      width="100%"
      sx={{ maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" }}
    >
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <Text
          fontWeight={"bold"}
          fontSize={15}
          mb={1}
          sx={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            display: "block",
            width: "100%",
          }}
        >
          {ind + 1 + ". " + item?.question}
        </Text>
      </Box>
      <Box
        display={"grid"}
        gridTemplateColumns={"repeat(2, 1fr)"}
        gap={1.5}
        mb={1}
        sx={{ width: "100%", maxWidth: "100%" }}
      >
        {tableData?.answerOptions?.map((it) => (
          <Flex key={it} alignItems={"center"} gap={1} sx={{ minWidth: 0 }}>
            <Radio
              disabled={!!disabled}
              checked={item?.answerOptions === it}
              value={it}
              onChange={() => handleRadioChange(ind, it)}
              size="small"
              sx={{ p: 0, flexShrink: 0 }}
            />
            <Text sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
              {replaceUnderScoreAddSpace(it?.toLowerCase())}
            </Text>
          </Flex>
        ))}
      </Box>
    </Box>
  ));

  // Desktop rendering (table)
  const rows = tableData?.screeners?.map((item: any, ind: number) => (
    <tr key={item.key}>
      <td>
        <Text>{ind + 1 + ". " + item?.question}</Text>
      </td>

      {tableData?.answerOptions?.map((it) => (
        <td key={it}>
          <Flex
            alignItems={"center"}
            minWidth={"160px"}
            justifyContent={"center"}
          >
            <Radio
              disabled={!!disabled}
              checked={item?.answerOptions === it}
              defaultChecked={false}
              value={it}
              onChange={() => handleRadioChange(ind, it)}
            />
          </Flex>
        </td>
      ))}
    </tr>
  ));

  return (
    <Flex
      width="100%"
      overflow={isMobile ? "hidden" : "auto"}
      flexDirection={"column"}
      sx={{ maxWidth: "100%", boxSizing: "border-box" }}
    >
      <Flex flexDirection={"column"} gap={1} mb={1.5}>
        <Text fontWeight={"medium"}>{titleDetail?.title}</Text>
        <Text fontWeight={"medium"}>{titleDetail?.subtitle}</Text>
        {/* {!showScore && (
              <IconEye
                style={{
                  marginLeft: "0.5rem",
                  verticalAlign: "middle",
                  cursor: "pointer",
                }}
                size={20}
                color={`${theme.colors.brand[5]}`}
                strokeWidth={2}
                onClick={handleEyeIconClick}
              />
            )} */}
        {/* {showScore && (
              <IconEyeOff
                style={{
                  marginLeft: "0.5rem",
                  verticalAlign: "middle",
                  cursor: "pointer",
                }}
                size={20}
                color={`${theme.colors.brand[5]}`}
                strokeWidth={2}
                onClick={handleBack}
              />
            )} */}
      </Flex>

      {isMobile ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Text
            fontWeight={"bold"}
            mb={2}
            sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
          >
            Over the last 2 weeks, how often have you been bothered by the
            following problems?
          </Text>
          {mobileRows}
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table style={{ minWidth: 500, width: "100%" }}>
            <thead style={{ width: "100%" }}>
              <tr>
                <th>
                  <Text fontWeight={"bold"}>
                    Over the last 2 weeks, how often have you been bothered by
                    the following problems?
                  </Text>
                </th>
                {tableData?.answerOptions?.map((it) => (
                  <th key={it}>
                    <Flex
                      alignItems={"center"}
                      minWidth={"160px"}
                      justifyContent={"center"}
                      width={"100%"}
                    >
                      <Text>
                        {replaceUnderScoreAddSpace(it?.toLowerCase())}
                      </Text>
                    </Flex>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        </Box>
      )}

      {showScore && (
        <>
          <Flex
            style={{
              padding: "10px 14px",
            }}
          >
            <Title order={6}>
              GAD-7 score obtained by adding score for each question (total
              points)
            </Title>
          </Flex>
          <Box
            style={{
              padding: "10px 14px",
              marginTop: "10px",
            }}
          >
            <Title order={6}>Interpretation:</Title>
          </Box>
          <Box>
            <div
              style={{
                padding: "10px 14px",
                marginTop: "3px",
                display: "flex",
              }}
            >
              <Title order={6}>
                When screening for anxiety disorders, a score of 8 or greater
                represents a reasonable cut-point for identifying probable cases
                of generalized anxiety disorder;
                <span style={{ fontSize: "15px,", fontWeight: 400 }}>
                  further diagnostic assessment is warranted to determine the
                  presence and type of anxiety disorder. Using a cut-off of 8
                  the GAD-7 has a sensitivity of 92% and specificity of 76% for
                  diagnosis of generalized anxiety disorder.
                </span>
              </Title>
            </div>
            <Box style={{ padding: "10px 14px" }}>
              <Text>
                The following cut-offs correlate with level of anxiety severity:
              </Text>

              <List>
                {/* <List.Item>Score 0-4: Minimal Anxiety</List.Item>
                  <List.Item>Score 5-9: Mild Anxiety</List.Item>
                  <List.Item>Score 10-14: Moderate Anxiety</List.Item>
                  <List.Item>Score greater than 15: Severe Anxiety</List.Item> */}
              </List>

              <Text>
                Based on a recent meta-analysis, some experts have recommended
                considering using a cut-off 8 in order to optimize sensitivity
                without compromising specificity2.
              </Text>
            </Box>
          </Box>
        </>
      )}
    </Flex>
  );
};

export default AllScreeningAssessment;
